import { OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications.constants';
import {
    OperatorCustomNotification,
    TrackedTrigger,
} from 'modules/settings/pods/settings-operator-custom-notifications/operator-custom-notification.vm';
import {
    OperatorCustomNotificationRow,
    TrackedEventGroup,
} from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications.model';

type TranslateFunction = (key: string) => string;

export interface OperatorCustomNotificationRowLabels {
    info: string;
    warning: string;
    alarm: string;
    reset: string;
    resetWithComment: string;
    noOptionalSettings: string;
    unknownModel: string;
}

export const toTrackedEventLabel = (trigger: Pick<TrackedTrigger, 'id' | 'displayName'>): string =>
    `${trigger.id}: ${trigger.displayName}`;

const compareGroupsWithUnknownLast =
    (unknownModelLabel: string) =>
    (first: TrackedEventGroup, second: TrackedEventGroup): number => {
        if (first.deviceTypeName === unknownModelLabel) {
            return 1;
        }
        if (second.deviceTypeName === unknownModelLabel) {
            return -1;
        }
        return first.deviceTypeName.localeCompare(second.deviceTypeName);
    };

export const groupTriggersByDeviceType = (
    triggers: TrackedTrigger[],
    resolveDeviceTypeName: (deviceTypeId: string) => string | undefined,
    unknownModelLabel: string
): TrackedEventGroup[] => {
    const labelsByDeviceTypeName = new Map<string, string[]>();

    triggers.forEach((trigger) => {
        const deviceTypeName = resolveDeviceTypeName(trigger.deviceTypeId) ?? unknownModelLabel;
        const existing = labelsByDeviceTypeName.get(deviceTypeName) ?? [];
        labelsByDeviceTypeName.set(deviceTypeName, [...existing, toTrackedEventLabel(trigger)]);
    });

    return [...labelsByDeviceTypeName.entries()]
        .map(([deviceTypeName, eventLabels]) => ({ deviceTypeName, eventLabels }))
        .sort(compareGroupsWithUnknownLast(unknownModelLabel));
};

const toNotificationType = (
    notification: OperatorCustomNotification,
    labels: OperatorCustomNotificationRowLabels
): string => {
    const enabled: string[] = [];

    if (notification.grayNotification.enabled) {
        enabled.push(labels.info);
    }
    if (notification.yellowNotification.enabled) {
        enabled.push(labels.warning);
    }
    if (notification.redNotification.enabled) {
        enabled.push(labels.alarm);
    }

    return enabled.join(', ');
};

const toOptionalSettings = (
    notification: OperatorCustomNotification,
    labels: OperatorCustomNotificationRowLabels
): string => {
    if (notification.isResettable && notification.requiresCommentOnReset) {
        return labels.resetWithComment;
    }
    if (notification.isResettable) {
        return labels.reset;
    }
    return labels.noOptionalSettings;
};

export const toOperatorCustomNotificationRow = (
    notification: OperatorCustomNotification,
    resolveDeviceTypeName: (deviceTypeId: string) => string | undefined,
    labels: OperatorCustomNotificationRowLabels
): OperatorCustomNotificationRow => ({
    id: notification.id,
    notificationName: notification.name,
    trackedEvents: groupTriggersByDeviceType(notification.trackedTriggers, resolveDeviceTypeName, labels.unknownModel),
    monitoringPeriodDays: notification.windowDays,
    notificationType: toNotificationType(notification, labels),
    optionalSettings: toOptionalSettings(notification, labels),
    active: notification.active,
});

export const buildOperatorCustomNotificationRowLabels = (
    t: TranslateFunction
): OperatorCustomNotificationRowLabels => ({
    info: t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.notification-type.info`),
    warning: t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.notification-type.warning`),
    alarm: t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.notification-type.alarm`),
    reset: t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.optional-settings.reset`),
    resetWithComment: t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.optional-settings.reset-with-comment`),
    noOptionalSettings: t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.optional-settings.none`),
    unknownModel: t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.tracked-events.unknown-model`),
});
